import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";
import {
  useClearCaseImages,
  usePublishCase,
  useUnpublishCase,
  type TestCase,
} from "@/api/tests-api";

import { TestCaseUploader, summarise } from "./TestCaseUploader";

/**
 * Add to, or replace, a case's images after it was first uploaded.
 *
 * Uploading used to happen once, in the moment a case was created, and
 * never again — so a study uploaded from the wrong folder, or missing a
 * series, could only be abandoned and rebuilt.
 *
 * There is no in-place swap of a single frame. "Replace image 4" has no
 * meaning when the frames are a stack the viewer walks, so replacing is
 * clear-then-upload, said plainly.
 *
 * Both actions are refused once a candidate has been assigned the case.
 * A recording of somebody describing these pictures is the answer they
 * were judged on; changing the pictures underneath it leaves a review
 * that cannot be read.
 */
export function CaseImagesModal({
  testCase,
  onClose,
  onChanged,
}: {
  testCase: TestCase | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const clear = useClearCaseImages();
  const publish = usePublishCase();
  const unpublish = useUnpublishCase();
  const [uploading, setUploading] = useState(false);
  // Set only by pressing Publish a second time, after the server has
  // refused once and said what was missing.
  const [acceptedIncomplete, setAcceptedIncomplete] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // EVERY HOOK ABOVE THIS LINE. The modal is mounted permanently by
  // TestLibraryPanel with a null case, so `testCase` going null -> set
  // is a re-render of the SAME instance -- and any hook declared below
  // the early return changes the hook COUNT between those two renders.
  // React throws "Rendered more hooks than during the previous render"
  // and unmounts the whole admin tree, which presents as the page
  // freezing on the click rather than as an error.
  if (!testCase) return null;

  const locked = testCase.assigned_count > 0;
  const published = testCase.status.toUpperCase() === "READY";
  const archived = testCase.status.toUpperCase() === "ARCHIVED";

  const fail = (err: unknown, fallback: string) => {
    const detail =
      (err as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail ?? fallback;
    toast({ title: detail, variant: "error" });
  };

  const setPublished = async (next: boolean) => {
    try {
      if (next) {
        await publish.mutateAsync({
          caseId: testCase.id,
          // ASKED, NOT ASSUMED. The server refuses the first attempt and
          // names what was lost; this is the admin answering it. There
          // is no dialog here because the shortfall is already on screen
          // above -- they are looking at the number when they decide.
          acceptIncomplete: acceptedIncomplete,
        });
        toast({ title: `“${testCase.title}” published` });
      } else {
        await unpublish.mutateAsync(testCase.id);
        toast({ title: `“${testCase.title}” is a draft again` });
      }
      onChanged();
    } catch (err) {
      const res = (err as {
        response?: { status?: number; data?: { detail?: string } };
      })?.response;
      if (
        next &&
        res?.status === 422 &&
        (res.data?.detail ?? "").includes("could not be stored")
      ) {
        // Arm the override and say so. The next press carries it.
        setAcceptedIncomplete(true);
        toast({
          title: res.data?.detail ?? "",
          description: "Press Publish again to accept the case as it is.",
          variant: "error",
        });
        return;
      }
      fail(err, next ? "Could not publish this case" : "Could not unpublish");
    }
  };


  const clearAll = async () => {
    try {
      await clear.mutateAsync(testCase.id);
      onChanged();
      toast({ title: "Images removed — upload the replacement study" });
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not remove the images";
      toast({ title: detail, variant: "error" });
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) {
          setUploading(false);
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="case-images-modal"
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                Images — {testCase.title}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                {testCase.instance_count} image
                {testCase.instance_count === 1 ? "" : "s"} stored ·{" "}
                {archived
                  ? "archived"
                  : published
                    ? "published — exams may use it"
                    : "draft — no exam may use it yet"}
              </Dialog.Description>
              {/* THE SHORTFALL, WHERE THE CASE IS. It has been recorded
                  on every case since uploads existed and nothing read
                  it, so a study missing eight hundred slices looked
                  exactly like a complete one. */}
              {testCase.failed_instance_count > 0 && (
                <p
                  data-cy="case-images-shortfall"
                  className="mt-1 text-xs font-medium text-status-wip"
                >
                  {testCase.failed_instance_count} file
                  {testCase.failed_instance_count === 1 ? "" : "s"} from the
                  upload could not be stored — this case may be missing
                  images.
                </p>
              )}
            </div>
            <Dialog.Close
              data-cy="btn-close-case-images"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="px-6 py-5">
            {locked ? (
              <p
                data-cy="case-images-locked"
                className="rounded-md border border-border bg-background px-4 py-3 text-sm text-foreground"
              >
                {testCase.assigned_count} candidate
                {testCase.assigned_count === 1 ? " has" : "s have"} been
                assigned this case, so its images can no longer be changed —
                their recordings describe these pictures. Archive it and build
                a new case instead.
              </p>
            ) : uploading ? (
              <TestCaseUploader
                caseId={testCase.id}
                onDone={(summary) => {
                  setUploading(false);
                  onChanged();
                  toast({
                    title: summarise(summary),
                    variant: summary.stored > 0 ? "success" : "error",
                  });
                }}
              />
            ) : (
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  data-cy="btn-add-images"
                  onClick={() => setUploading(true)}
                  className="sk-btn sk-btn-primary self-start"
                >
                  {testCase.instance_count === 0
                    ? "Upload images"
                    : "Add more images"}
                </button>
                <p className="text-xs text-foreground-muted">
                  Adding keeps what is already here. A file already in the case
                  is recognised and skipped, so re-dropping the same folder is
                  safe.
                </p>

                {testCase.instance_count > 0 && (
                  <>
                    <hr className="my-1 border-border" />
                    <button
                      type="button"
                      data-cy="btn-clear-images"
                      onClick={() => setConfirmClear(true)}
                      className="self-start rounded-md border border-border px-4 py-2 text-sm font-semibold text-destructive hover:bg-table-row-hover"
                    >
                      Remove all images
                    </button>
                    <p className="text-xs text-foreground-muted">
                      To REPLACE the study, remove these first and then upload
                      the new folder. There is no per-image swap — the frames
                      are a stack, not a list of slots.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Publishing lives here rather than as a row icon: this is
                where a case is finished, and the action needs a word. */}
            {!archived && !uploading && (
              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-border pt-4">
                {published ? (
                  <button
                    type="button"
                    data-cy="btn-unpublish-case"
                    onClick={() => {
                      void setPublished(false);
                    }}
                    className="sk-btn"
                  >
                    Back to draft
                  </button>
                ) : (
                  <button
                    type="button"
                    data-cy="btn-publish-case"
                    disabled={testCase.instance_count === 0 || publish.isPending}
                    title={
                      testCase.instance_count === 0
                        ? "Upload at least one image before publishing"
                        : "Make this case available to exams"
                    }
                    onClick={() => {
                      void setPublished(true);
                    }}
                    className="sk-btn sk-btn-primary"
                  >
                    Publish
                  </button>
                )}
                <p className="text-[11px] text-foreground-muted">
                  {published
                    ? "Taking it back to draft is refused while an exam still lists it."
                    : "A draft is kept and can be previewed, but no exam may include it until it is published."}
                </p>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Nested inside a Radix dialog on purpose: AppDialog renders its
          own fixed overlay at a higher z-index, so it sits over this
          modal rather than behind it. */}
      <AppDialog
        open={confirmClear}
        cy="dialog-clear-images"
        tone="danger"
        title="Remove every image?"
        body={`All ${testCase.instance_count} image${
          testCase.instance_count === 1 ? "" : "s"
        } are deleted from “${testCase.title}”, and the case drops back to draft until you upload again. Candidates already sealed with this case keep it.`}
        onDismiss={() => setConfirmClear(false)}
        actions={[
          {
            label: "Remove them",
            cy: "btn-confirm-clear-images",
            variant: "danger",
            onClick: () => {
              setConfirmClear(false);
              void clearAll();
            },
          },
          {
            label: "Keep them",
            cy: "btn-cancel-clear-images",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setConfirmClear(false),
          },
        ]}
      />
    </Dialog.Root>
  );
}
